import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { getConsent, setConsent, type ConsentValue } from "@/lib/analyticsConsent";

/**
 * Bottom-of-screen cookie consent banner. Shown only when the user has not
 * yet made a choice (`getConsent() === null`). Reject-all is the default —
 * no analytics SDK boots until the user clicks Accept.
 */
export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(getConsent() === null);
  }, []);

  const decide = (value: ConsentValue) => {
    setConsent(value);
    // Notify same-tab listeners (storage event only fires across tabs).
    window.dispatchEvent(new CustomEvent("excerptus:consent-changed", { detail: value }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-14 z-50 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-lg md:bottom-0"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <p className="text-sm text-muted-foreground">
          Excerptus uses essential cookies to run the app and a cookieless, anonymous analytics
          tool (Plausible) so we can see which features people use. No cross-site tracking, no
          ads, no personal data. Click <strong>Reject</strong> to opt out of analytics, or manage
          this any time on the{" "}
          <a href="/cookies" className="text-primary underline">
            Cookie Policy
          </a>{" "}
          page.
        </p>

        <div className="flex flex-shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={() => decide("denied")}>
            Reject
          </Button>
          <Button size="sm" onClick={() => decide("granted")}>
            OK
          </Button>

        </div>
      </div>
    </div>
  );
};


